import type { PendingVersionSummary } from '../api/types'

interface PendingChangeIndicatorProps {
  pendingInfo: PendingVersionSummary
  size?: 'small' | 'medium'
  showFieldCount?: boolean
  onClick?: () => void
}

// Warning/clock icon for pending changes
const PendingIcon = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
)

export function PendingChangeIndicator({
  pendingInfo,
  size = 'small',
  showFieldCount = true,
  onClick,
}: PendingChangeIndicatorProps) {
  const formatSource = (sourceType: string, vendor?: string | null): string => {
    if (sourceType === 'vendor_email' && vendor) {
      return vendor
    }
    return sourceType === 'app_user' ? 'User' : sourceType
  }

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const tooltipText = `Pending changes from ${formatSource(pendingInfo.sourceType, pendingInfo.sourceVendor)} (${formatDate(pendingInfo.createdAt)})`

  const isSmall = size === 'small'

  return (
    <span
      className={`pending-indicator ${isSmall ? 'pending-indicator-small' : 'pending-indicator-medium'}`}
      title={tooltipText}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <PendingIcon size={isSmall ? 12 : 14} />
      {showFieldCount && (
        <span className="pending-indicator-count">
          {pendingInfo.fieldCount}
        </span>
      )}
    </span>
  )
}

// Compact dot indicator for timeline view
export function PendingDot({ onClick }: { onClick?: () => void }) {
  return (
    <span
      className="pending-dot"
      title="Has pending changes"
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    />
  )
}
